import type { AnswerValue, Choice, Question } from '@/types/form'
import type {
  CalculationResult,
  FormCalculationRule,
  FormVariableDefinition,
  FormVariableFormat,
  OutcomeBranch,
} from '@/types/form-variables'

export function createDefaultFormVariables(): FormVariableDefinition[] {
  return [
    {
      id: 'var-hidden-sales-code',
      key: 'salesCode',
      label: '業務代碼',
      kind: 'hidden',
      format: 'text',
      description: '由分享連結或 hidden field 帶入，用於追蹤業務來源。',
      defaultValue: 'DEMO',
    },
    {
      id: 'var-total-score',
      key: 'totalScore',
      label: '總分',
      kind: 'score',
      format: 'number',
      description: '依選項分數加總的 mock-safe 分數變數。',
      defaultValue: 0,
    },
    {
      id: 'var-total-amount',
      key: 'totalAmount',
      label: '預估金額',
      kind: 'money',
      format: 'currency',
      description: '依選項金額加總的 mock-safe 金額變數。',
      defaultValue: 0,
    },
    {
      id: 'var-qualified-score',
      key: 'qualifiedScore',
      label: '資格分',
      kind: 'calculated',
      format: 'number',
      description: '可用於結果頁分流的前端計算變數。',
      defaultValue: 0,
    },
  ]
}

export function createDefaultCalculationRules(questions: Question[]): FormCalculationRule[] {
  const choiceQuestionIds = questions
    .filter((question) => question.choices.length > 0)
    .map((question) => question.id)
  const numericQuestionIds = questions
    .filter((question) => ['NUMBER', 'NUMBER_SLIDER', 'TOTAL'].includes(question.type))
    .map((question) => question.id)

  return [
    {
      id: 'calc-total-score',
      variableKey: 'totalScore',
      label: '選項分數加總',
      mode: 'sum',
      sourceQuestionIds: choiceQuestionIds,
      format: 'number',
    },
    {
      id: 'calc-total-amount',
      variableKey: 'totalAmount',
      label: '選項金額加總',
      mode: 'sum',
      sourceQuestionIds: choiceQuestionIds,
      format: 'currency',
    },
    {
      id: 'calc-numeric-average',
      variableKey: 'qualifiedScore',
      label: '數字題平均',
      mode: 'average',
      sourceQuestionIds: numericQuestionIds,
      format: 'number',
    },
  ]
}

export function createDefaultOutcomeBranches(): OutcomeBranch[] {
  return [
    {
      id: 'outcome-high-score',
      label: '高分完成頁',
      description: '總分達標時顯示積極 CTA。',
      operator: 'AND',
      conditions: [{ field: 'totalScore', operator: 'greater_or_equal', value: 10 }],
      target: 'completion_page',
      completionTitle: '符合優先跟進條件',
      completionDescription: '建議安排業務或客服主動聯繫。',
    },
    {
      id: 'outcome-high-amount',
      label: '高金額結果頁',
      description: '預估金額較高時導向高價值客戶流程。',
      operator: 'AND',
      conditions: [{ field: 'totalAmount', operator: 'greater_or_equal', value: 100000 }],
      target: 'result_page',
      completionTitle: '高價值需求',
      completionDescription: '可在後續 API/DB 批次接正式結果頁設定。',
    },
    {
      id: 'outcome-default',
      label: '預設完成頁',
      description: '未命中其他結果分流時使用目前完成頁。',
      operator: 'AND',
      conditions: [],
      target: 'completion_page',
      completionTitle: '已收到您的回饋',
      completionDescription: '使用問卷完成頁設定。',
    },
  ]
}

export function calculateChoiceTotals(
  questions: Question[],
  answers: Map<string, AnswerValue>,
): { score: number; amount: number; selectedChoices: Choice[] } {
  const selectedChoices: Choice[] = []

  for (const question of questions) {
    const answer = answers.get(question.id)
    if (answer === undefined || answer === null) continue
    const answerValues = new Set(
      Array.isArray(answer) ? answer.map(String) : [String(extractPrimitiveAnswer(answer))],
    )
    for (const choice of question.choices) {
      if (answerValues.has(choice.value)) selectedChoices.push(choice)
    }
  }

  return {
    score: selectedChoices.reduce((total, choice) => total + Number(choice.score ?? 0), 0),
    amount: selectedChoices.reduce((total, choice) => total + Number(choice.amount ?? 0), 0),
    selectedChoices,
  }
}

export function evaluateCalculationRule(
  rule: FormCalculationRule,
  questions: Question[],
  answers: Map<string, AnswerValue>,
): CalculationResult {
  const sourceQuestions = questions.filter((question) =>
    rule.sourceQuestionIds.includes(question.id),
  )
  const values = sourceQuestions
    .map((question) => numericValueForQuestion(question, answers.get(question.id)))
    .filter((value) => Number.isFinite(value))
  const choiceTotals = calculateChoiceTotals(sourceQuestions, answers)
  let value = 0

  if (rule.variableKey === 'totalAmount') {
    value = choiceTotals.amount
  } else if (rule.variableKey === 'totalScore') {
    value = choiceTotals.score
  } else {
    switch (rule.mode) {
      case 'average':
        value = values.length ? values.reduce((total, item) => total + item, 0) / values.length : 0
        break
      case 'count':
        value = values.length
        break
      case 'weighted':
        value = values.reduce((total, item) => total + item * Number(rule.weight ?? 1), 0)
        break
      case 'custom':
      case 'sum':
        value = values.reduce((total, item) => total + item, 0)
        break
      default:
        value = 0
        break
    }
  }

  return {
    key: rule.variableKey,
    label: rule.label,
    value,
    formattedValue: formatVariableValue(value, rule.format ?? 'number'),
    sourceQuestionIds: rule.sourceQuestionIds,
    source: 'mock-safe',
  }
}

export function evaluateCalculationRules(
  rules: FormCalculationRule[],
  questions: Question[],
  answers: Map<string, AnswerValue>,
): CalculationResult[] {
  return rules.map((rule) => evaluateCalculationRule(rule, questions, answers))
}

export function formatVariableValue(value: number | string, format: FormVariableFormat) {
  if (format === 'text') return String(value)
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return '0'
  if (format === 'currency') {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      maximumFractionDigits: 0,
    }).format(numeric)
  }
  if (format === 'percent') {
    return `${numeric.toLocaleString('zh-TW', { maximumFractionDigits: 1 })}%`
  }
  return numeric.toLocaleString('zh-TW', { maximumFractionDigits: 2 })
}

function numericValueForQuestion(question: Question, answer: AnswerValue | undefined) {
  if (answer === undefined || answer === null) return Number.NaN
  if (question.choices.length > 0) {
    return calculateChoiceTotals([question], new Map([[question.id, answer]])).score
  }
  const primitive = extractPrimitiveAnswer(answer)
  return Number(primitive)
}

function extractPrimitiveAnswer(answer: AnswerValue) {
  if (Array.isArray(answer)) return answer[0] ?? ''
  if (answer && typeof answer === 'object') {
    const firstValue = Object.values(answer)[0]
    return Array.isArray(firstValue) ? (firstValue[0] ?? '') : (firstValue ?? '')
  }
  return answer
}
